import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { ArrowRight, Play, ChevronRight, Zap, Search, Clock } from 'lucide-react';

const EnhancedHero = () => {  
  const [searchPhase, setSearchPhase] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedTerms, setHighlightedTerms] = useState([]);

  const searchQueries = [
    "payment service is returning 500s since 3pm",
    "api latency spiked to 2000ms in us-east-1",
    "cpu usage at 100% after latest deploy",
    "authentication failures started 15min ago",
    "database connection timeouts in prod"
  ];

  const generateChangelogEvents = () => {
    const events = [
      {
        id: 1,
        service: 'payment-service',
        type: 'deploy',
        description: 'Deployed payment-service v2.3.4 with updated rate limiting',
        timestamp: '15:02:34',
        confidence: 0.95
      },
      {
        id: 2,
        service: 'payment-service',
        type: 'config',
        description: 'Modified circuit breaker thresholds for external APIs',
        timestamp: '14:58:12',
        confidence: 0.88
      },
      {
        id: 3,
        service: 'api-gateway',
        type: 'scale',
        description: 'Scaled API gateway instances from 5 to 8',
        timestamp: '14:45:00',
        confidence: 0.92
      },
      {
        id: 4,
        service: 'auth-service',
        type: 'restart',
        description: 'Rolling restart of auth service pods',
        timestamp: '14:30:15',
        confidence: 0.85
      },
      {
        id: 5,
        service: 'database-primary',
        type: 'config',
        description: 'Updated connection pool settings and timeout values',
        timestamp: '14:15:22',
        confidence: 0.91
      }
    ];

    // Add more events with lower confidence
    return [...events, ...Array.from({ length: 15 }, (_, i) => ({
      id: i + 6,
      service: events[Math.floor(Math.random() * events.length)].service,
      type: ['deploy', 'config', 'scale', 'restart', 'update'][Math.floor(Math.random() * 5)],
      description: 'Earlier system change...',
      timestamp: '13:' + String(Math.floor(Math.random() * 60)).padStart(2, '0') + ':' + String(Math.floor(Math.random() * 60)).padStart(2, '0'),
      confidence: 0.3 + (Math.random() * 0.4)
    }))];
  };

  const changelogEvents = React.useMemo(() => generateChangelogEvents(), []);

  const scrollToDemo = () => {
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Enhanced text transition animation
  useEffect(() => {
    let timeoutId;
    const currentQuery = searchQueries[searchPhase];
    
    const typeText = (text, index = 0) => {
      if (index <= text.length) {
        setSearchText(text.slice(0, index));
        timeoutId = setTimeout(() => {
          typeText(text, index + 1);
        }, 50);
      } else {
        setIsTyping(false);
        setIsSearching(true);
        // Start search animation
        timeoutId = setTimeout(() => {
          setIsSearching(false);
          // Move to next phase after showing results
          timeoutId = setTimeout(() => {
            setSearchPhase((prev) => (prev + 1) % searchQueries.length);
          }, 4000);
        }, 1000);
      }
    };

    setIsTyping(true);
    setIsSearching(false);
    typeText(currentQuery);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [searchPhase]);

  // Enhanced results filtering with highlighting
  const [displayedEvents, setDisplayedEvents] = useState([]);
  
  useEffect(() => {
    const getRelevantTerms = (phase) => {
      const query = searchQueries[phase].toLowerCase();
      return {
        service: phase === 0 ? 'payment-service' :
                phase === 1 ? 'api-gateway' :
                phase === 2 ? 'database-primary' :
                phase === 3 ? 'auth-service' :
                'database-primary',
        terms: query.split(' ').filter(term => term.length > 3)
      };
    };

    const updateResults = () => {
      const { service, terms } = getRelevantTerms(searchPhase);
      setHighlightedTerms(terms);
      
      const filtered = changelogEvents
        .filter(event => event.service === service)
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 5);
      
      setDisplayedEvents(filtered);
    };

    if (!isTyping && !isSearching) {
      const timeout = setTimeout(updateResults, 300);
      return () => clearTimeout(timeout);
    }
  }, [isTyping, isSearching, searchPhase, changelogEvents]);

  return (
    <div className="relative min-h-screen flex items-center bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Background elements remain the same */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 opacity-40" />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100 rounded-full opacity-20 blur-3xl animate-pulse" />
        <div className="absolute top-20 -left-20 w-72 h-72 bg-indigo-100 rounded-full opacity-20 blur-3xl animate-pulse" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-12 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left column remains the same */}
          <div className="max-w-2xl">
            <Button
              onClick={scrollToDemo} 
              className="inline-flex items-center px-4 py-2 mb-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg hover:shadow-xl transition-shadow">
              <Zap className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">New: Changelog Semantic Search</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6 leading-tight">
              Find Breaking Changes in <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Seconds</span>, Not Hours
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Outages are inevitable—prolonged downtime isn't. 
            DeltaOps gives you the answers you need, instantly.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button size="lg" className="text-base px-8 shadow-lg hover:scale-105 transition-transform"
                onClick={() => window.location.href = 'https://console.deltaops.app/signup'}
              >
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                onClick={scrollToDemo} 
                className="text-base px-8 border-2"
              >
                View Demo <Play className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Enhanced Right Column */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 rounded-2xl transform rotate-3" />
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
              {/* Enhanced Search Interface */}
              <div className="mb-6">
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Search className={`w-5 h-5 ${isSearching ? 'text-blue-500 animate-pulse' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    value={searchText}
                    readOnly
                    className="flex-1 bg-transparent border-none focus:outline-none text-gray-800 placeholder-gray-400"
                    placeholder="Describe the incident or issue..."
                  />
                  {isTyping && (
                    <div className="w-2 h-5 bg-blue-500 animate-pulse rounded" />
                  )}
                  {isSearching && (
                    <svg
                      viewBox="0 0 24 24"
                      className="w-5 h-5 text-blue-500 animate-spin"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 2L2 19H22L12 2z" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Enhanced Results Section */}
              <div className="space-y-4 h-[400px] overflow-hidden">
                {displayedEvents.map((event, index) => (
                  <div
                    key={event.id}
                    className={`p-4 rounded-lg transition-all duration-300 ${
                      isSearching ? 'opacity-0 translate-y-4' :
                      'opacity-100 translate-y-0'
                    } bg-gray-50 hover:bg-gray-100 border border-gray-100 relative overflow-hidden`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    {/* Git-style commit hash */}
                    <div className="absolute left-4 top-0 h-full w-1 bg-gray-200" />
                    <div className="absolute left-3.5 top-4 w-2 h-2 rounded-full bg-gray-400" />
                    
                    <div className="ml-4">
                      {/* Service and Type Indicators */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          event.type === 'config' ? 'bg-purple-100 text-purple-700' :
                          event.type === 'deploy' ? 'bg-green-100 text-green-700' :
                          event.type === 'scale' ? 'bg-blue-100 text-blue-700' :
                          event.type === 'restart' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {event.type}
                        </span>
                        <div className="flex-1 h-3 bg-gray-200 rounded animate-pulse" />
                      </div>

                      {/* Abstract Content Lines */}
                      <div className="space-y-2">
                        <div className="h-2 bg-gray-200 rounded w-3/4" />
                        <div className="h-2 bg-gray-200 rounded w-1/2" />
                      </div>

                      {/* Relevance Score */}
                      <div className="mt-3 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: isSearching ? '0%' : `${event.confidence * 100}%`,
                              backgroundColor: event.confidence > 0.8 ? '#22c55e' : // green-500
                                            event.confidence > 0.6 ? '#eab308' : // yellow-500
                                            '#ef4444' // red-500
                            }}
                          />
                        </div>
                        <div className="text-xs font-medium text-gray-700">
                          {Math.round(event.confidence * 100)}%
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedHero;